// --- Constants ---
const EPSILON = 1e-6;
const ARC_SEGMENTS = 16; // Points for full circle arc approximation
/** Angle dedup threshold (radians) — angles closer than this are considered identical */
const ANGLE_DEDUP_THRESHOLD = 1e-9;
// --- Wall filtering ---
/**
 * Определяет, блокирует ли стена видимость.
 * Ethereal и open doors не блокируют.
 */
export function isWallBlocking(wall) {
    if (wall.blocking === 'ethereal')
        return false;
    if (wall.blocking === 'window')
        return false;
    if (wall.blocking === 'door' && wall.doorState === 'open')
        return false;
    return true;
}
/**
 * Определяет, блокирует ли стена свет.
 * Аналогично isWallBlocking, но окна пропускают свет (light passes through windows).
 */
export function isWallBlockingLight(wall) {
    if (wall.blocking === 'ethereal')
        return false;
    if (wall.blocking === 'window')
        return false;
    if (wall.blocking === 'door' && wall.doorState === 'open')
        return false;
    return true;
}
// --- Geometry helpers ---
/** Ray-segment intersection. Returns intersection point and distance, or null. */
function raySegmentIntersection(ox, oy, dx, dy, x1, y1, x2, y2, sx, sy) {
    // D × S (2D cross product of ray direction and segment direction)
    const denom = dx * sy - dy * sx;
    if (Math.abs(denom) < EPSILON)
        return null; // parallel
    // Vector from ray origin to segment start: (A - O)
    const qpx = x1 - ox;
    const qpy = y1 - oy;
    // t1 = (A-O) × S / (D × S) — ray parameter
    const t1 = (qpx * sy - qpy * sx) / denom;
    if (t1 < EPSILON)
        return null; // behind ray or at origin
    // t2 = (A-O) × D / (D × S) — segment parameter
    const t2 = (qpx * dy - qpy * dx) / denom;
    if (t2 < -EPSILON || t2 > 1 + EPSILON)
        return null; // outside segment
    return {
        point: { x: ox + dx * t1, y: oy + dy * t1 },
        distance: t1,
    };
}
/** Check if a wall is degenerate (zero length) */
function isDegenerateWall(wall) {
    return Math.abs(wall.x1 - wall.x2) < EPSILON && Math.abs(wall.y1 - wall.y2) < EPSILON;
}
/**
 * Pre-compute wall data for optimized raycasting.
 * Sorts walls by distance from origin for early termination.
 */
function prepareWalls(origin, walls) {
    const ox = origin.x;
    const oy = origin.y;
    const prepared = new Array(walls.length);
    for (let i = 0; i < walls.length; i++) {
        const w = walls[i];
        const sx = w.x2 - w.x1;
        const sy = w.y2 - w.y1;
        // Midpoint distance squared
        const mx = (w.x1 + w.x2) * 0.5 - ox;
        const my = (w.y1 + w.y2) * 0.5 - oy;
        // Min endpoint distance squared
        const d1sq = (w.x1 - ox) * (w.x1 - ox) + (w.y1 - oy) * (w.y1 - oy);
        const d2sq = (w.x2 - ox) * (w.x2 - ox) + (w.y2 - oy) * (w.y2 - oy);
        prepared[i] = {
            x1: w.x1, y1: w.y1,
            x2: w.x2, y2: w.y2,
            sx, sy,
            distSq: mx * mx + my * my,
            minEndpointDistSq: d1sq < d2sq ? d1sq : d2sq,
        };
    }
    // Sort by distance — closer walls first for early termination
    prepared.sort((a, b) => a.minEndpointDistSq - b.minEndpointDistSq);
    return prepared;
}
/**
 * Collect unique endpoint angles from walls relative to origin.
 * Optimized: uses numerical comparison instead of string-based Set.
 */
function collectEndpointAngles(origin, walls) {
    // Collect all angles (2 per wall)
    const raw = new Float64Array(walls.length * 2);
    let count = 0;
    for (let i = 0; i < walls.length; i++) {
        const w = walls[i];
        raw[count++] = Math.atan2(w.y1 - origin.y, w.x1 - origin.x);
        raw[count++] = Math.atan2(w.y2 - origin.y, w.x2 - origin.x);
    }
    // Sort for deduplication
    const sorted = raw.subarray(0, count).sort();
    // Deduplicate: remove angles within ANGLE_DEDUP_THRESHOLD
    const angles = [];
    if (sorted.length > 0) {
        angles.push(sorted[0]);
        for (let i = 1; i < sorted.length; i++) {
            if (sorted[i] - sorted[i - 1] > ANGLE_DEDUP_THRESHOLD) {
                angles.push(sorted[i]);
            }
        }
    }
    return angles;
}
/**
 * Cast a ray at given angle, find nearest wall intersection.
 * Optimized: uses pre-sorted walls with early termination.
 * If maxRange > 0 and no wall hit within maxRange, clamp to maxRange.
 */
function castRay(origin, angle, walls, maxRange) {
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const ox = origin.x;
    const oy = origin.y;
    // Initialize to maxRange so t1 >= nearestDist prunes walls beyond vision range immediately
    let nearestDist = maxRange > 0 ? maxRange : Infinity;
    let nearestPx = 0;
    let nearestPy = 0;
    let found = false;
    for (let i = 0; i < walls.length; i++) {
        const w = walls[i];
        // Note: no early termination by endpoint distance — long walls can have
        // far endpoints but intersect the ray much closer (e.g., a vertical wall
        // passing through the vision circle with both endpoints outside it).
        // D × S (2D cross product of ray direction and segment direction)
        const denom = dx * w.sy - dy * w.sx;
        if (Math.abs(denom) < EPSILON)
            continue; // parallel
        // Vector from ray origin to segment start: (A - O)
        const qpx = w.x1 - ox;
        const qpy = w.y1 - oy;
        // t1 = (A-O) × S / (D × S) — ray parameter
        const t1 = (qpx * w.sy - qpy * w.sx) / denom;
        if (t1 < EPSILON)
            continue; // behind ray or at origin
        if (t1 >= nearestDist)
            continue; // can't beat current nearest
        // t2 = (A-O) × D / (D × S) — segment parameter
        const t2 = (qpx * dy - qpy * dx) / denom;
        if (t2 < -EPSILON || t2 > 1 + EPSILON)
            continue; // outside segment
        nearestDist = t1;
        nearestPx = ox + dx * t1;
        nearestPy = oy + dy * t1;
        found = true;
    }
    // If maxRange > 0 and wall hit is beyond range, clamp
    if (maxRange > 0) {
        if (!found || nearestDist > maxRange) {
            return {
                point: { x: ox + dx * maxRange, y: oy + dy * maxRange },
                angle,
                clamped: true,
            };
        }
    }
    if (found) {
        return { point: { x: nearestPx, y: nearestPy }, angle, clamped: false };
    }
    // No intersection, no range limit — ray goes to large distance
    const fallback = 10000;
    return {
        point: { x: ox + dx * fallback, y: oy + dy * fallback },
        angle,
        clamped: false,
    };
}
// --- Main algorithm ---
/**
 * Вычисляет visibility polygon из точки origin.
 * Optimized angular sweep: O(N log N) where N = number of wall endpoints.
 * Uses distance-sorted walls with early termination for O(N log N) effective complexity.
 *
 * @param origin — точка наблюдения (canvas coordinates)
 * @param walls — массив стен (уже отфильтрованных SpatialIndex.query)
 * @param maxRange — максимальная дальность видимости (px). 0 = unlimited (bounding box)
 * @returns — упорядоченный по углу массив точек visibility polygon
 */
export function computeVisibilityPolygon(origin, walls, maxRange) {
    // Filter degenerate walls
    const validWalls = walls.filter(w => !isDegenerateWall(w));
    // No walls — return bounding circle/box
    if (validWalls.length === 0) {
        if (maxRange > 0) {
            return generateCircle(origin, maxRange, ARC_SEGMENTS);
        }
        const BIG = 10000;
        return [
            { x: origin.x - BIG, y: origin.y - BIG },
            { x: origin.x + BIG, y: origin.y - BIG },
            { x: origin.x + BIG, y: origin.y + BIG },
            { x: origin.x - BIG, y: origin.y + BIG },
        ];
    }
    // Pre-compute wall data and sort by distance
    const prepared = prepareWalls(origin, validWalls);
    // Collect unique angles from wall endpoints
    const baseAngles = collectEndpointAngles(origin, validWalls);
    // For each unique angle, cast 3 rays: angle - epsilon, angle, angle + epsilon
    const endpointAngles = new Array(baseAngles.length * 3);
    for (let i = 0; i < baseAngles.length; i++) {
        const a = baseAngles[i];
        const j = i * 3;
        endpointAngles[j] = a - EPSILON;
        endpointAngles[j + 1] = a;
        endpointAngles[j + 2] = a + EPSILON;
    }
    // Add sentinel angles only in directions NOT covered by wall endpoints.
    const SWEEP_COUNT = 64;
    const SENTINEL_EXCLUSION = 0.18; // ~10° — exclude sentinels within this of any endpoint angle
    const filteredSentinels = [];
    for (let i = 0; i < SWEEP_COUNT; i++) {
        const sa = -Math.PI + (2 * Math.PI * i) / SWEEP_COUNT;
        let tooClose = false;
        for (let j = 0; j < baseAngles.length; j++) {
            let diff = sa - baseAngles[j];
            if (diff > Math.PI)
                diff -= 2 * Math.PI;
            if (diff < -Math.PI)
                diff += 2 * Math.PI;
            if (Math.abs(diff) < SENTINEL_EXCLUSION) {
                tooClose = true;
                break;
            }
        }
        if (!tooClose)
            filteredSentinels.push(sa);
    }
    const allAngles = new Array(endpointAngles.length + filteredSentinels.length);
    for (let i = 0; i < endpointAngles.length; i++)
        allAngles[i] = endpointAngles[i];
    for (let i = 0; i < filteredSentinels.length; i++)
        allAngles[endpointAngles.length + i] = filteredSentinels[i];
    // Sort by angle
    allAngles.sort((a, b) => a - b);
    // Cast rays using prepared (distance-sorted) walls
    const rays = new Array(allAngles.length);
    for (let i = 0; i < allAngles.length; i++) {
        rays[i] = castRay(origin, allAngles[i], prepared, maxRange);
    }
    // Build polygon: insert arc segments between consecutive range-clamped points
    if (maxRange > 0) {
        return buildPolygonWithArcs(origin, rays, maxRange);
    }
    const result = new Array(rays.length);
    for (let i = 0; i < rays.length; i++) {
        result[i] = rays[i].point;
    }
    return result;
}
/**
 * Build final polygon, inserting arc segments on the range circle
 * between consecutive clamped points.
 */
function buildPolygonWithArcs(origin, rays, maxRange) {
    const result = [];
    for (let i = 0; i < rays.length; i++) {
        const curr = rays[i];
        const next = rays[(i + 1) % rays.length];
        result.push(curr.point);
        // If both current and next are clamped (on the circle), add arc between them
        if (curr.clamped && next.clamped) {
            let angleDiff = next.angle - curr.angle;
            while (angleDiff > Math.PI)
                angleDiff -= 2 * Math.PI;
            while (angleDiff < -Math.PI)
                angleDiff += 2 * Math.PI;
            const absAngle = Math.abs(angleDiff);
            if (absAngle > EPSILON * 10) {
                const numSegments = Math.max(1, Math.round((absAngle / (2 * Math.PI)) * ARC_SEGMENTS));
                const step = angleDiff / numSegments;
                for (let j = 1; j < numSegments; j++) {
                    const a = curr.angle + step * j;
                    result.push({
                        x: origin.x + Math.cos(a) * maxRange,
                        y: origin.y + Math.sin(a) * maxRange,
                    });
                }
            }
        }
    }
    return result;
}
/** Generate circle approximation */
function generateCircle(center, radius, segments) {
    const points = [];
    for (let i = 0; i < segments; i++) {
        const angle = (2 * Math.PI * i) / segments;
        points.push({
            x: center.x + Math.cos(angle) * radius,
            y: center.y + Math.sin(angle) * radius,
        });
    }
    return points;
}
// --- Cache ---
/**
 * Создаёт кэш для результатов visibility вычислений.
 * LRU eviction при превышении maxEntries.
 */
export function createVisibilityCache(maxEntries = 100) {
    const cache = new Map();
    function makeKey(origin, wallsVersion, maxRange) {
        return `${origin.x},${origin.y},${wallsVersion},${maxRange}`;
    }
    return {
        get(origin, wallsVersion, maxRange) {
            const key = makeKey(origin, wallsVersion, maxRange);
            const value = cache.get(key);
            if (value === undefined)
                return null;
            // Move to end (most recently used)
            cache.delete(key);
            cache.set(key, value);
            return value;
        },
        set(origin, wallsVersion, maxRange, polygon) {
            const key = makeKey(origin, wallsVersion, maxRange);
            if (cache.has(key)) {
                cache.delete(key);
            }
            cache.set(key, polygon);
            // LRU eviction
            if (cache.size > maxEntries) {
                const oldestKey = cache.keys().next().value;
                if (oldestKey !== undefined) {
                    cache.delete(oldestKey);
                }
            }
        },
        clear() {
            cache.clear();
        },
        get size() {
            return cache.size;
        },
    };
}
// --- Line-of-Sight check ---
/**
 * Check if there is a clear line of sight between two points.
 * Returns true if no blocking wall intersects the segment from→to.
 *
 * Optimization: bounding-box pre-filter skips walls whose AABB doesn't
 * overlap with the ray's AABB (O(1) per wall).
 *
 * @param from  — origin point (canvas pixels)
 * @param to    — target point (canvas pixels)
 * @param walls — wall array from PageState
 * @returns true if line of sight is clear (no blocking wall between from and to)
 */
export function hasLineOfSight(from, to, walls) {
    // Ray direction
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < EPSILON)
        return true; // same point — trivially visible
    // AABB of the ray segment (with small margin)
    const rayMinX = Math.min(from.x, to.x) - EPSILON;
    const rayMaxX = Math.max(from.x, to.x) + EPSILON;
    const rayMinY = Math.min(from.y, to.y) - EPSILON;
    const rayMaxY = Math.max(from.y, to.y) + EPSILON;
    // Normalized ray direction
    const ndx = dx / length;
    const ndy = dy / length;
    for (const wall of walls) {
        // Skip non-blocking walls
        if (!isWallBlocking(wall))
            continue;
        // Bounding-box pre-filter: skip walls outside ray AABB
        const wallMinX = Math.min(wall.x1, wall.x2);
        const wallMaxX = Math.max(wall.x1, wall.x2);
        const wallMinY = Math.min(wall.y1, wall.y2);
        const wallMaxY = Math.max(wall.y1, wall.y2);
        if (wallMaxX < rayMinX || wallMinX > rayMaxX)
            continue;
        if (wallMaxY < rayMinY || wallMinY > rayMaxY)
            continue;
        // Ray-segment intersection check
        const sx = wall.x2 - wall.x1;
        const sy = wall.y2 - wall.y1;
        const intersection = raySegmentIntersection(from.x, from.y, ndx, ndy, wall.x1, wall.y1, wall.x2, wall.y2, sx, sy);
        if (intersection !== null && intersection.distance <= length + EPSILON) {
            return false; // blocked
        }
    }
    return true; // clear LoS
}
